import { omit } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SortDirections } from '../../../../../common/src/domain/sortColumnsSchema';
import { Roles } from '../../../../../common/src/enums/Roles';
import { SurveyStates } from '../../../../../common/src/enums/SurveyStates';
import { getInspectionStandardTypeOptions } from '../../../customerSettings';
import { customInspectionTypeFrontEndSettings } from '../../../customInspectionTypes/customInspectionTypeFrontEndSettings';
import { IWithReactRouterProps } from '../../../mixins/router/withReactRouter';
import { useTranslate } from '../../../mixins/translate/translate';
import { fetchDelete, fetchGet } from '../../../store/fetch';
import { IStoreState } from '../../../store/IStoreState';
import { openApiFetchGet } from '../../../store/openApiFetch';
import { useOpenApiFetch } from '../../../store/useOpenApiFetch';
import { InspectionStandardTypes } from '../../../utils/enums/InspectionStandardTypes';
import { SortColumn } from '../../../utils/sorting';
import { EditPanelFilterMultiSelectFromEndpoint } from '../../common/editPanel/EditPanelFilterMultiSelectFromEndpoint';
import { EditPanelFilterSelectFromEndpoint } from '../../common/editPanel/EditPanelFilterSelectFromEndpoint';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    Content,
    EditPanelButton,
    EditPanelButtons,
    EditPanelDatePicker,
    EditPanelFilterMultiSelect,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelSection,
    EditPanelSelect,
    ISelectOption,
    PanelBody,
    PanelWithoutForm,
    RightTray,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableEmphasizedText,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
    TableSortableHeader,
    TableSubText,
    Topbar,
    TopbarCrumbActive,
    TopBarDownloadButton,
    TopbarLeft,
    TopbarRight,
    Tray,
} from '../../elements';
import { ISurveyListItem } from '../../interfaces/ISurveyListItem';
import { FormatCondition } from '../../Objects/FormatCondition';
import { Spinner } from '../../spinners';
import { transformLabelsToSelectOptions } from '../../utils/entityLabels.utils';
import { ListPager } from '../../utils/ListPager';
import { NullableFormattedDate } from '../../utils/NullableFormattedDate';
import { SurveyStatusLabel } from '../../utils/SurveyStatusLabel';
import { TagLabels } from '../../utils/TagLabels';
import { useReactAsync } from '../../utils/useReactAsync';
import { useSearch } from '../../utils/useSearch';
import { SurveyListBulkUpdateButton } from './SurveyListBulkUpdateButton';

const itemsPerPage = 50;

type SearchAttributes = {
    inspectionStandardType?: undefined | string;
    conditions?: undefined | string[];
    ownerCompanyId?: undefined | string;
    surveyorOperatorCompanyId?: undefined | string;
    batchId?: undefined | string;
    location?: undefined | string;
    status?: undefined | string;
    riskAssessment?: undefined | string;
    labels: string[];
    description?: undefined | string;
    conductedPeriodStartDate?: undefined | string | null;
    conductedPeriodEndDate?: undefined | string | null;
    surveryedOn?: undefined | { greaterThanOrEqual?: string; lessThanOrEqual?: string };
};

async function fetchBatches(inspectionStandardType: undefined | string): Promise<ISelectOption[]> {
    const queryParameters = {} as { [key: string]: string };
    if (inspectionStandardType) {
        queryParameters.inspectionStandardType = inspectionStandardType;
    }
    const result = await fetchGet<{ items: { id: string; name: string }[] }>('/api/batchesList', queryParameters, [], [403]);

    const items = result.json?.items;
    if (items && items.length > 0) {
        const valueTextArray = items.map((item) => ({
            value: item.id,
            text: `${item.name.substring(0, 42)}${item.name.length > 43 ? '...' : ''}`,
        }));
        return valueTextArray;
    }
    return [];
}

type Pagination = { limit: string; offset: string; sortColumns: SortColumn[] };

async function fetchSurveys(params: { query: Pagination & SearchAttributes }) {
    const result = await openApiFetchGet('/api/surveys', queryObjectToQueryStringValues(params.query));

    return result as { items: ISurveyListItem[]; total: number };
}

function queryObjectToQueryStringValues(query: Pagination & SearchAttributes) {
    const queryParameters = {
        ...omit(query, ['conductedPeriodStartDate', 'conductedPeriodEndDate', 'ownerCompanyId', 'riskAssessment']),
        companyId: query.ownerCompanyId,
        conditions: query.conditions?.join(),
        risk: query.riskAssessment,
    };

    return queryParameters;
}

export function SurveysList(props: IWithReactRouterProps) {
    const translate = useTranslate();

    const { navigate } = props;

    const useBatches = useSelector((state: IStoreState) => state.authentication.useBatches);
    const languageCode = useSelector((state: IStoreState) => state.language.code);
    const userRole = useSelector((state: IStoreState) => state.authentication.role);
    const isClient = useSelector((state: IStoreState) => state.authentication.isClient);
    const useProductTitle = useSelector((state: IStoreState) => state.authentication.useProductTitle);
    const areLabelsVisibleInOverviews = useSelector((state: IStoreState) => state.authentication.areLabelsVisibleInOverviews);

    const { handleSortChanged, handleNavigation, queryObject, searchParameters, setSearchAttribute } = useSearch<SearchAttributes>(
        {
            labels: [],
        },
        [{ columnName: 'surveys.surveryedOn', direction: SortDirections.Descending }],
        itemsPerPage,
        'SurveysList',
    );

    const [description, setDescription] = React.useState<string>(searchParameters.description ?? '');
    const [location, setLocation] = React.useState<string>(searchParameters.location ?? '');
    const { inspectionStandardType, conductedPeriodStartDate, conductedPeriodEndDate } = searchParameters;

    const [batches, setBatches] = useState<ISelectOption[]>([]);

    const args = { query: queryObject };
    const { data, reload, isLoading } = useReactAsync(fetchSurveys, args, [queryObject]);
    const { data: surveysPermissions } = useOpenApiFetch('/api/surveys/permissions', 'get', {});

    const isMounted = useRef(false);

    useEffect(() => {
        if (useBatches) {
            const load = async () => {
                const fetchedBatches = await fetchBatches(inspectionStandardType);
                setBatches(fetchedBatches);
            };
            void load();
        }

        if (isMounted.current) {
            // Only set the search attribute if the component has been mounted, to avoid overriding the search parameters from the store
            setSearchAttribute({ batchId: undefined, conditions: undefined });
        } else {
            // Set the ref to true after the first render
            isMounted.current = true;
        }
    }, [inspectionStandardType, useBatches, setSearchAttribute]);

    useEffect(() => {
        let surveryedOn = {};

        if (conductedPeriodStartDate && !conductedPeriodEndDate) {
            surveryedOn = { greaterThanOrEqual: conductedPeriodStartDate };
        } else if (!conductedPeriodStartDate && conductedPeriodEndDate) {
            const end = new Date(conductedPeriodEndDate);
            end.setDate(end.getDate() + 1);
            const endDateString = end.toISOString().split('T')[0];
            surveryedOn = { lessThanOrEqual: endDateString };
        } else if (conductedPeriodStartDate && conductedPeriodEndDate) {
            const end = new Date(conductedPeriodEndDate);
            end.setDate(end.getDate() + 1);
            const endDateString = end.toISOString().split('T')[0];
            surveryedOn = { greaterThanOrEqual: conductedPeriodStartDate, lessThanOrEqual: endDateString };
        }

        setSearchAttribute({ surveryedOn });
    }, [conductedPeriodStartDate, conductedPeriodEndDate, setSearchAttribute]);

    const isAdminOrClientSurveyorOperator =
        (isClient && (userRole === Roles.Operator || userRole === Roles.Surveyor)) || userRole === Roles.Administrator;
    const isAdminOrClientOwner = (isClient && userRole === Roles.Owner) || userRole === Roles.Administrator;

    const surveyStates = () => {
        const options = [
            { value: SurveyStates.Open, text: translate(`surveyStatus.${SurveyStates.Open}`) },
            { value: SurveyStates.Preparation, text: translate(`surveyStatus.${SurveyStates.Preparation}`) },
            { value: SurveyStates.Conducting, text: translate(`surveyStatus.${SurveyStates.Conducting}`) },
            { value: SurveyStates.Delivery, text: translate(`surveyStatus.${SurveyStates.Delivery}`) },
            { value: SurveyStates.Reporting, text: translate(`surveyStatus.${SurveyStates.Reporting}`) },
            { value: SurveyStates.Completed, text: translate(`surveyStatus.${SurveyStates.Completed}`) },
        ];
        if (userRole === Roles.Administrator) {
            options.push({ value: SurveyStates.Deleted, text: translate(`surveyStatus.${SurveyStates.Deleted}`) });
        }
        return options;
    };

    const handleChange = (value: unknown, stateKey: string) => {
        setSearchAttribute({ [stateKey]: value });
    };

    const deleteSurvey = async (surveyId: string) => {
        const url = `/api/surveys/${surveyId}`;
        await fetchDelete(url);

        await reload();
    };

    const editSurvey = (surveyId: string) => {
        navigate(`/surveys/${surveyId}/summaryAndAdvice`);
    };

    const rowClicked = (surveyId: string) => {
        navigate(`/surveys/${surveyId}`);
    };

    const resetForm = () => {
        setLocation('');
        setDescription('');
        setSearchAttribute({
            batchId: undefined,
            conditions: undefined,
            ownerCompanyId: undefined,
            inspectionStandardType: undefined,
            labels: [],
            location: undefined,
            surveyorOperatorCompanyId: undefined,
            riskAssessment: undefined,
            status: undefined,
            description: undefined,
            conductedPeriodStartDate: undefined,
            conductedPeriodEndDate: undefined,
            surveryedOn: undefined,
        });
    };

    const handleOnSearchButtonClicked = () => {
        setSearchAttribute({ description, location });
    };

    return (
        <>
            <Topbar>
                <TopbarLeft>
                    <TopbarCrumbActive title={useProductTitle ? translate('common.productReports') : translate('common.surveys')} icon="fa-camera" />
                </TopbarLeft>
                <TopbarRight>
                    <TopBarDownloadButton
                        path="/api/surveys/download/excel"
                        query={{
                            languageCode,
                            ...omit(queryObjectToQueryStringValues(queryObject), ['limit', 'offset', 'page', 'sortColumns']),
                        }}
                        text={translate('common.excelExport')}
                        disabled={data?.items.length === 0}
                    />
                    {surveysPermissions?.canBulkUpdate && (
                        <>
                            <SurveyListBulkUpdateButton
                                itemCount={data?.total ?? 0}
                                surveyStatusOptions={surveyStates()}
                                surveySearchQuery={omit(queryObjectToQueryStringValues(queryObject), ['limit', 'offset', 'page', 'sortColumns'])}
                                reload={reload}
                            />
                        </>
                    )}
                </TopbarRight>
            </Topbar>
            <Content>
                <Tray>
                    <PanelWithoutForm>
                        <PanelBody noPadding={true}>
                            <Table hover={true}>
                                <TableHead>
                                    <TableHeadRow light={true}>
                                        <TableSortableHeader
                                            header={translate('common.object')}
                                            columnName="objects.name"
                                            sortColumns={searchParameters.sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader
                                            header={useProductTitle ? translate('common.template') : translate('common.inspectionTemplate')}
                                            columnName="surveys.inspectionStandardType"
                                            sortColumns={searchParameters.sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader
                                            header={translate('common.date')}
                                            columnName="surveys.surveryedOn"
                                            sortColumns={searchParameters.sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader
                                            header={translate('common.result')}
                                            columnName="surveys.condition"
                                            sortColumns={searchParameters.sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableSortableHeader
                                            align="right"
                                            header={translate('common.status')}
                                            columnName="surveys.status"
                                            sortColumns={searchParameters.sortColumns}
                                            onClick={handleSortChanged}
                                        />
                                        <TableHeader align="right">&nbsp; </TableHeader>
                                    </TableHeadRow>
                                </TableHead>
                                {!data || isLoading ? (
                                    <TableBody>
                                        <TableRow>
                                            <TableData colSpan={5}>
                                                <Spinner />
                                            </TableData>
                                        </TableRow>
                                    </TableBody>
                                ) : (
                                    <TableBody>
                                        {data.items.map((item) => (
                                            <TableRow onClick={() => rowClicked(item.id)} key={item.id}>
                                                <TableData>
                                                    <TableEmphasizedText text={item.name} />
                                                    <TableSubText text={item.description} />
                                                    {areLabelsVisibleInOverviews && <TagLabels labels={item.labels} />}
                                                </TableData>
                                                <TableData>
                                                    {(item.inspectionStandardType && (
                                                        <span>{translate(`inspectionStandard.${item.inspectionStandardType}`)}</span>
                                                    )) || <span>NEN 2767</span>}
                                                </TableData>
                                                <TableData nowrap={true}>
                                                    <NullableFormattedDate value={item.surveryedOn} />
                                                </TableData>
                                                <TableData>
                                                    <FormatCondition
                                                        condition={item.condition}
                                                        careCondition={item.careCondition}
                                                        inspectionStandardType={item.inspectionStandardType}
                                                    />
                                                </TableData>
                                                <TableData alignRight={true}>
                                                    <SurveyStatusLabel value={item.status} />
                                                </TableData>
                                                <TableButtons>
                                                    {item.canEdit && <IconEditButton onClick={() => editSurvey(item.id)} />}
                                                    {item.canDelete && (
                                                        <IconRemoveButton
                                                            removeQuestion={translate('common.confirmDelete', item.description)}
                                                            onClick={async () => deleteSurvey(item.id)}
                                                        />
                                                    )}
                                                </TableButtons>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </PanelBody>
                        <ListPager
                            disabled={isLoading}
                            itemsPerPage={itemsPerPage}
                            total={data?.total ?? 0}
                            page={searchParameters.page}
                            handleNavigation={handleNavigation}
                        />
                    </PanelWithoutForm>
                </Tray>
                <RightTray>
                    <form>
                        <div className="admin-form">
                            <h4> {useProductTitle ? translate('product.filterProduct') : translate('survey.findSurvey')}</h4>
                            <EditPanelSection topBorder={true}>
                                <EditPanelInput
                                    name="description"
                                    placeholder={translate('survey.descriptionOrNumber')}
                                    value={description}
                                    icon="fa fa-building"
                                    handleChange={setDescription}
                                    handleEnter={() => setSearchAttribute({ description })}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelFilterSelect
                                    name="inspectionStandardType"
                                    value={searchParameters.inspectionStandardType}
                                    handleChange={handleChange}
                                    disabled={isLoading}
                                    placeholder={
                                        useProductTitle
                                            ? translate('common.selectItem', translate('product.type'))
                                            : translate('common.selectItem', translate('common.inspectionType'))
                                    }
                                    options={getInspectionStandardTypeOptions(translate)}
                                />
                            </EditPanelSection>
                            {searchParameters.inspectionStandardType && (
                                <>
                                    {customInspectionTypeFrontEndSettings[searchParameters.inspectionStandardType] ? (
                                        <>
                                            {customInspectionTypeFrontEndSettings[searchParameters.inspectionStandardType]!
                                                .getSurveyConditionFilterOptions && (
                                                <EditPanelSection>
                                                    <EditPanelFilterMultiSelect
                                                        name="conditions"
                                                        value={searchParameters.conditions ?? []}
                                                        handleChange={handleChange}
                                                        disabled={isLoading}
                                                        placeholder={translate('common.selectItem', translate('common.result'))}
                                                        options={customInspectionTypeFrontEndSettings[searchParameters.inspectionStandardType]!
                                                            .getSurveyConditionFilterOptions!()}
                                                    />
                                                </EditPanelSection>
                                            )}
                                        </>
                                    ) : (
                                        <EditPanelSection>
                                            <EditPanelFilterMultiSelect
                                                name="conditions"
                                                value={searchParameters.conditions ?? []}
                                                handleChange={handleChange}
                                                disabled={isLoading}
                                                placeholder={translate('common.selectItem', translate('common.result'))}
                                                options={[
                                                    { value: '1', text: translate('condition.excellent') },
                                                    { value: '2', text: translate('condition.good') },
                                                    { value: '3', text: translate('condition.reasonable') },
                                                    { value: '4', text: translate('condition.moderate') },
                                                    { value: '5', text: translate('condition.poor') },
                                                    { value: '6', text: translate('condition.bad') },
                                                    { value: 'F', text: translate('condition.further') },
                                                    { value: 'U', text: translate('condition.unknown') },
                                                ]}
                                            />
                                        </EditPanelSection>
                                    )}
                                    {searchParameters.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                                        <EditPanelSection>
                                            <EditPanelSelect
                                                name="riskAssessment"
                                                value={searchParameters.riskAssessment}
                                                handleChange={handleChange}
                                                disabled={isLoading}
                                                placeholder={translate('common.selectItem', translate('survey.riskConstruction'))}
                                                options={[
                                                    { value: '1', text: translate('condition.orMore', translate('cra.riskScore1')) },
                                                    { value: '2', text: translate('condition.orMore', translate('cra.riskScore2')) },
                                                    { value: '3', text: translate('condition.orMore', translate('cra.riskScore3')) },
                                                    { value: '4', text: translate('cra.riskScore4') },
                                                ]}
                                            />
                                        </EditPanelSection>
                                    )}
                                </>
                            )}
                            {isAdminOrClientSurveyorOperator && (
                                <>
                                    <EditPanelSection>
                                        <EditPanelFilterSelectFromEndpoint
                                            name="ownerCompanyId"
                                            value={searchParameters.ownerCompanyId}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            placeholder={translate('common.selectItem', translate('common.owner'))}
                                            url="/api/companyList"
                                            queryParameters={{ type: 'owner' }}
                                            mapResponseToOptions={(response: { json: { items: { id: string; name: string }[] } }) =>
                                                response.json.items.map((i) => ({ text: i.name, value: i.id }))
                                            }
                                        />
                                    </EditPanelSection>
                                </>
                            )}
                            {isAdminOrClientOwner && (
                                <>
                                    <EditPanelSection>
                                        <EditPanelFilterSelectFromEndpoint
                                            name="surveyorOperatorCompanyId"
                                            value={searchParameters.surveyorOperatorCompanyId}
                                            onChange={handleChange}
                                            disabled={isLoading}
                                            placeholder={translate('common.selectItem', translate('common.surveyor'))}
                                            url="/api/companyList"
                                            queryParameters={{ type: 'surveyor|operator|administrator' }}
                                            mapResponseToOptions={(response: { json: { items: { id: string; name: string }[] } }) =>
                                                response.json.items.map((i) => ({ text: i.name, value: i.id }))
                                            }
                                        />
                                    </EditPanelSection>
                                </>
                            )}
                            <EditPanelSection>
                                <EditPanelFilterSelect
                                    name="status"
                                    value={searchParameters.status}
                                    handleChange={handleChange}
                                    disabled={isLoading}
                                    placeholder={translate('common.selectItem', translate('common.status'))}
                                    options={surveyStates()}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelFilterMultiSelectFromEndpoint
                                    name="labels"
                                    value={searchParameters.labels}
                                    onChange={handleChange}
                                    disabled={isLoading}
                                    placeholder={translate('common.selectItem', translate('common.labels'))}
                                    url="/api/surveys/knownLabels"
                                    mapResponseToOptions={(response: { json: string[] }) => transformLabelsToSelectOptions(response?.json)}
                                    isHiddenWhenNoOptions
                                />
                            </EditPanelSection>
                            {useBatches && (
                                <EditPanelSection>
                                    <EditPanelFilterSelect
                                        name="batchId"
                                        value={searchParameters.batchId}
                                        handleChange={handleChange}
                                        disabled={isLoading}
                                        placeholder={translate('common.selectItem', 'Batch')}
                                        options={batches}
                                    />
                                </EditPanelSection>
                            )}
                            <EditPanelSection>
                                <EditPanelInput
                                    name="location"
                                    placeholder={translate('common.location')}
                                    value={location}
                                    icon="fa fa-map-marker"
                                    handleChange={setLocation}
                                    handleEnter={() => setSearchAttribute({ location })}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelDatePicker
                                    name="conductedPeriodStartDate"
                                    value={searchParameters.conductedPeriodStartDate}
                                    handleChange={handleChange}
                                    topLabel={useProductTitle ? translate('common.dateRange') : translate('common.conductedPeriod')}
                                    placeholder={translate('common.startDate')}
                                />
                            </EditPanelSection>
                            <EditPanelSection>
                                <EditPanelDatePicker
                                    name="conductedPeriodEndDate"
                                    value={searchParameters.conductedPeriodEndDate}
                                    handleChange={handleChange}
                                    placeholder={translate('common.endDate')}
                                />
                            </EditPanelSection>
                            <EditPanelButtons topBorder>
                                <EditPanelButton onClick={resetForm} text={translate('common.reset')} disabled={isLoading} />
                                <EditPanelButton
                                    onClick={handleOnSearchButtonClicked}
                                    text={translate('common.search')}
                                    primary
                                    disabled={isLoading}
                                />
                            </EditPanelButtons>
                        </div>
                    </form>
                </RightTray>
            </Content>
        </>
    );
}
